import type { CSSProperties, JSX } from 'react';
import type { Palette } from '../../../../_theme/palette.js';
import { hexWithAlpha } from '../../../../_theme/palette.js';
import type { SurveyQuestion } from '../../types.js';

interface MarkdownFieldProps {
  palette: Palette;
  question: SurveyQuestion;
}

// Read-only Markdown renderer matching the main app's QuestionPreview /
// simpleMarkdown so survey authors get the same rendering across builder
// preview, public link, and SDK shell. Supports headings, lists, blockquote,
// fenced code, hr, inline code, bold/italic/strikethrough, links, images.
//
// Input is HTML-escaped first then string-replaced to inject the allowed
// tags. No <script>/<style>/on* survives the escape pass, and the iframe
// shell already isolates everything from the host page.
export function MarkdownField({ palette, question }: MarkdownFieldProps): JSX.Element {
  const raw = question.settings?.markdownContent ?? '';
  return (
    <div
      style={wrapperStyle(palette)}
      dangerouslySetInnerHTML={{ __html: simpleMarkdown(raw) }}
    />
  );
}

function simpleMarkdown(md: string): string {
  const lines = md.split('\n');
  const blocks: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i] ?? '';
    const escaped = escapeHtml(line);

    if (escaped.trim() === '') {
      blocks.push('');
      i++;
      continue;
    }

    const heading = escaped.match(/^(#{1,6}) (.+)$/);
    if (heading && heading[1] && heading[2]) {
      const level = heading[1].length;
      blocks.push(`<h${level}>${inlineFormat(heading[2])}</h${level}>`);
      i++;
      continue;
    }

    if (/^[-*_]{3,}$/.test(escaped.trim())) {
      blocks.push('<hr/>');
      i++;
      continue;
    }

    if (escaped.trimStart().startsWith('```')) {
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !(lines[i] ?? '').trimStart().startsWith('```')) {
        codeLines.push(escapeHtml(lines[i] ?? ''));
        i++;
      }
      i++;
      blocks.push(`<pre><code>${codeLines.join('\n')}</code></pre>`);
      continue;
    }

    if (escaped.startsWith('&gt; ') || escaped === '&gt;') {
      const quoteLines: string[] = [];
      while (i < lines.length) {
        const ql = escapeHtml(lines[i] ?? '');
        if (ql.startsWith('&gt; ')) quoteLines.push(ql.slice(5));
        else if (ql === '&gt;') quoteLines.push('');
        else break;
        i++;
      }
      blocks.push(`<blockquote>${simpleMarkdown(quoteLines.join('\n'))}</blockquote>`);
      continue;
    }

    if (/^\d+\. /.test(escaped)) {
      const items: string[] = [];
      while (i < lines.length) {
        const olLine = escapeHtml(lines[i] ?? '');
        const match = olLine.match(/^\d+\. (.+)$/);
        if (match) {
          items.push(`<li>${inlineFormat(match[1] ?? '')}</li>`);
          i++;
        } else break;
      }
      blocks.push(`<ol>${items.join('')}</ol>`);
      continue;
    }

    if (/^[*-] /.test(escaped)) {
      const items: string[] = [];
      while (i < lines.length) {
        const ulLine = escapeHtml(lines[i] ?? '');
        const match = ulLine.match(/^[*-] (.+)$/);
        if (match) {
          items.push(`<li>${inlineFormat(match[1] ?? '')}</li>`);
          i++;
        } else break;
      }
      blocks.push(`<ul>${items.join('')}</ul>`);
      continue;
    }

    blocks.push(inlineFormat(escaped));
    i++;
  }

  // Collapse consecutive paragraph lines into <p>…<br/>… blocks.
  const out: string[] = [];
  let para: string[] = [];
  const flushPara = (): void => {
    if (para.length === 0) return;
    out.push(`<p>${para.join('<br/>')}</p>`);
    para = [];
  };
  for (const block of blocks) {
    if (block === '') {
      flushPara();
      continue;
    }
    if (
      block.startsWith('<h') ||
      block.startsWith('<hr') ||
      block.startsWith('<pre') ||
      block.startsWith('<ul') ||
      block.startsWith('<ol') ||
      block.startsWith('<blockquote')
    ) {
      flushPara();
      out.push(block);
    } else {
      para.push(block);
    }
  }
  flushPara();
  return out.join('');
}

function inlineFormat(text: string): string {
  return text
    .replace(
      /!\[([^\]]*)\]\(([^)]+)\)/g,
      '<img src="$2" alt="$1" style="max-width:100%;border-radius:6px" />',
    )
    .replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
    )
    .replace(
      /`([^`]+)`/g,
      '<code style="background:rgba(127,127,127,0.1);padding:1px 4px;border-radius:3px;font-size:0.9em">$1</code>',
    )
    .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/~~(.+?)~~/g, '<del>$1</del>');
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function wrapperStyle(palette: Palette): CSSProperties {
  return {
    fontSize: 14,
    lineHeight: 1.6,
    color: palette.text,
    padding: '14px 16px',
    borderRadius: 12,
    border: `1px solid ${hexWithAlpha(palette.textMuted, 0.18)}`,
    background: hexWithAlpha(palette.primary, 0.04),
  };
}
